import { useEffect, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { Link as RouterLink, Navigate } from "react-router-dom";
import { useAuth } from "../context/auth";
import GenkanBackground from "../features/home/approaches";
import KiokuSanpai from "../features/en/KiokuSanpai";
import { useEnConfig } from "../features/en/useEn";
import { loadPending, savePending, PENDING_MAX } from "../features/en/pendingKioku";
import TochiCategory from "../features/tochi/TochiCategory";
import { LOGIN_OPEN } from "../config/loginGate";

const SERIF = '"Hiragino Mincho ProN", "Yu Mincho", serif';
// 扉「無料で始める」。config/loginGate の LOGIN_OPEN に従う
const DOOR_ENABLED = LOGIN_OPEN;
// 扉の下の「これまでに参った神社を、思い出す」「近くの神社を探す」。2026-10-31 ひとまず非表示
const QUIET_LINKS_ENABLED = false;

// 夜の参道パレット（/home 専用）
const WASHI = "#ece5d4";
const WASHI_DIM = "rgba(222,215,198,0.55)";
const WASHI_FAINT = "rgba(222,215,198,0.38)";
const GOLD = "#d4b46e";
const GOLD_TEXT = "#e6d3a3";

// 扉のボタン — 金の縁取りと余白で誘う（塗り潰しの金ピカにしない）
const DOOR_SX = {
  fontFamily: SERIF,
  fontWeight: 600,
  px: 7,
  py: 1.6,
  fontSize: "0.95rem",
  letterSpacing: 6,
  textIndent: 6,
  color: GOLD_TEXT,
  bgcolor: "rgba(212,180,110,0.05)",
  border: "1px solid rgba(212,180,110,0.5)",
  borderRadius: 0,
  boxShadow: "0 0 24px rgba(212,180,110,0.1)",
  transition: "all 0.3s ease",
  "&:hover": {
    bgcolor: "rgba(212,180,110,0.12)",
    borderColor: "rgba(224,201,143,0.9)",
    color: "#f2e5c0",
    boxShadow: "0 0 32px rgba(212,180,110,0.22)",
  },
};

// 扉の下の、静かな行き先
const QUIET_SX = {
  color: WASHI_DIM,
  fontSize: "0.8rem",
  fontFamily: SERIF,
  letterSpacing: 2,
  "&:hover": { color: WASHI, bgcolor: "rgba(255,255,255,0.04)" },
};

const KANJI = ["〇", "一", "二", "三", "四", "五", "六", "七", "八", "九", "十"];
const kanji = (n) => (n >= 0 && n <= 10 ? KANJI[n] : String(n));

/** 金の飾り罫 */
function GoldRule({ mb }) {
  return (
    <Box
      sx={{
        width: 52,
        height: "1px",
        mx: "auto",
        mb,
        background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
      }}
    />
  );
}

/** 玄関で思い出した社（この端末に預かっている分）。× で外せる */
function PendingChips({ list, onRemove }) {
  if (!list.length) return null;
  return (
    <Stack direction="row" flexWrap="wrap" justifyContent="center" sx={{ gap: 1, mb: 4 }}>
      {list.map((s) => (
        <Box
          key={s.shrineId}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.6,
            border: "1px solid rgba(212,180,110,0.45)",
            bgcolor: "rgba(212,180,110,0.06)",
          }}
        >
          <Typography sx={{ fontFamily: SERIF, color: GOLD_TEXT, fontSize: "0.85rem", letterSpacing: 1 }}>
            {s.name || s.shrineId}
          </Typography>
          <Box
            component="button"
            type="button"
            aria-label={`${s.name || s.shrineId}を外す`}
            onClick={() => onRemove(s.shrineId)}
            sx={{
              all: "unset",
              cursor: "pointer",
              color: WASHI_DIM,
              fontSize: "0.8rem",
              lineHeight: 1,
              px: 0.3,
              "&:hover": { color: WASHI },
            }}
          >
            ×
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

/** 預かりの約束。位置も時刻も聞かず、サーバーにも送らない */
function Azukari() {
  return (
    <Typography
      sx={{
        fontFamily: SERIF,
        textAlign: "center",
        fontSize: "0.7rem",
        letterSpacing: 1.5,
        lineHeight: 2,
        color: WASHI_FAINT,
        mt: 5,
      }}
    >
      いつ参ったかは、聞きません。
      <br />
      この端末にだけ、記しておきます。
    </Typography>
  );
}

/**
 * / と /home ＝ 玄関（ゲスト専用）。ダッシュボードではない。一文とひとつの扉。
 * ログイン済みの人は /top（TopPage）へ転送する。
 *
 * 2026-10-14 作り込み（骨 v3 #8「入口は記憶の参拝」の前倒し・その前半）:
 *   - 扉の下に「これまでに参った神社を、思い出す」を置き、ログイン前に数社選べるようにした。
 *     選んだ社は localStorage に預かるだけ（サーバーには送らない）。/start で名を入れた直後に
 *     flushPendingKioku が本人の visits へ流し、/top で「同じ神社に参った人 N 人」になる。
 *   - N 人をゲストに見せる後半（未ログインで叩ける関数）は Phase A デプロイ後に。charter v3 の未決に記載。
 *   - config/en が止まっていれば（allowed=false）思い出す導線ごと出さず、従来の一文と扉だけになる。
 *   - ヘッダー右上の「無料で始める」は玄関では出さない（本文の扉と重複するため。AppShell 側）。
 *
 * 2026-10-26 「できること」の段（一文と扉の下。増やしていく前提）:
 *   - 一つ目は「歴史と人と土地をつなぐ」（TochiCategory）。説明 → 「宝蔵を開く（フリー）」 → 押してから位置を聞く →
 *     町字に頁があれば、その場に最初の一枚（あなたが立っているのは、相模国 大住郡 田村 ＋ 最古の出来事）。
 *     事前判定は位置 → 町字 → 市区町村ごとの小さな索引 c/<コード>.json だけ。index.json（1MB 超）と村の頁は押してから読む。
 *   - 位置は押すまで聞かない。保存しない。扉の下にその旨の注意書きを置く。
 *   - 段を増やすときは features/home/Category.js に題と文を渡して並べる。
 *
 * 2026-10-13 絞り込み（据え置き）:
 *   - ログイン後の状態は /top に移した。読み物・縁ログのティッカーは外した。
 *   - 玄関背景は「A 夜の参道」に固定（/?genkan=b 等で他案を確認できる）。
 *   - 人と人の縁の機能は、ここでは一切語らない（コピーに「出会い」「袖振り合う」を出さない）。
 */
export default function HomePage() {
  const { user, loading } = useAuth();
  const { allowed: enAllowed } = useEnConfig();

  // この端末に預かっている社（玄関で思い出した分）
  const [pending, setPending] = useState(() => loadPending());
  // "door" = 一文と扉 ／ "kioku" = 記憶の参拝を開いている
  const [stage, setStage] = useState("door");

  // ヒーローの入場（静かにフェード＋上昇）。MUI Fade は初回マウントで発火しないため
  // ゲストは静止したままだった。マウント後に entered を立てて自前で立ち上げる。
  // prefers-reduced-motion は初期値 true で即表示（アニメなし）。
  const [entered, setEntered] = useState(() => {
    if (typeof window === "undefined") return true;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return true;
    return false;
  });
  useEffect(() => {
    if (entered) return undefined;
    const t = window.setTimeout(() => setEntered(true), 40);
    return () => window.clearTimeout(t);
  }, [entered]);
  // 認証の判定が終わるまでは文を出さない（ログイン済みなら /top へ飛ぶので、玄関の文を一瞬見せない）
  const heroIn = !loading && entered;

  if (!loading && user) return <Navigate to="/top" replace />;

  const recorded = pending.length > 0;
  const showKioku = enAllowed && stage === "kioku";

  const handleDone = (list) => {
    setPending(savePending([...pending, ...list]));
    setStage("door");
  };
  const removeOne = (id) => setPending(savePending(pending.filter((s) => s.shrineId !== id)));

  // 一文とひとつの扉（ゲスト）。社を思い出したあとは、その数を一行にする
  const line = recorded ? `${kanji(pending.length)}社の縁が、\nここに記されました。` : "縁は、往きて、還る。";
  const sub = recorded ? "名をひとつ入れると、\nあなたの社に収まります。" : "一社、参るごとに、\nひとつ、縁が結ばれる。";
  const door = { label: "無料で始める", to: "/start" };
  const quiet = { label: "近くの神社を探す", to: "/nearby" };
  const canAddMore = pending.length < PENDING_MAX;
  const remember = recorded ? "もうひとつ、思い出す" : "これまでに参った神社を、思い出す";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "calc(100dvh - 180px)" }}>
      {/* 玄関背景（A 夜の参道に固定。/?genkan=b 等で他案を確認できる） */}
      <GenkanBackground />

      <Box
        sx={{
          maxWidth: { xs: 560, md: 640 },
          width: "100%",
          mx: "auto",
          px: 2.5,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          pt: { xs: 5, sm: 8 },
          pb: 8,
          textAlign: "center",
        }}
      >
        <Typography
          variant="h4"
          fontWeight={900}
          sx={{
            fontFamily: SERIF,
            fontSize: { xs: "2.1rem", md: "2.6rem" },
            letterSpacing: { xs: 8, md: 12 },
            textIndent: { xs: 8, md: 12 },
            mb: 2,
            color: "#f2ead8",
            textShadow: "0 0 28px rgba(255,214,140,0.18)",
          }}
        >
          古道
        </Typography>

        <GoldRule mb={{ xs: 3.5, sm: 4.5 }} />

        <Box
          sx={{
            opacity: heroIn ? 1 : 0,
            transform: heroIn ? "none" : "translateY(16px)",
            transition: "opacity 900ms ease, transform 900ms cubic-bezier(0.2,0.7,0.2,1)",
            willChange: "opacity, transform",
          }}
        >
          {showKioku ? (
            /* 記憶の参拝（ログイン前。選んだ社はこの端末に預かる） */
            <Box sx={{ textAlign: "left", maxWidth: 480, mx: "auto" }}>
              <KiokuSanpai
                fixed={pending}
                compact={recorded}
                onDone={handleDone}
                onCancel={() => setStage("door")}
              />
              <Azukari />
            </Box>
          ) : (
            <>
              <Typography
                sx={{
                  fontFamily: SERIF,
                  fontSize: line.includes("\n")
                    ? { xs: "1.35rem", sm: "1.6rem", md: "1.8rem" }
                    : { xs: "1.6rem", sm: "1.9rem", md: "2.15rem" },
                  fontWeight: 600,
                  letterSpacing: line.includes("\n") ? 2 : 5,
                  lineHeight: 2.1,
                  whiteSpace: "pre-line",
                  color: WASHI,
                  mb: 2.5,
                }}
              >
                {line}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  fontFamily: SERIF,
                  color: WASHI_DIM,
                  fontSize: { xs: "0.85rem", md: "0.95rem" },
                  letterSpacing: 1.5,
                  lineHeight: 2.2,
                  whiteSpace: "pre-line",
                  mb: recorded ? 3 : 5,
                }}
              >
                {sub}
              </Typography>

              {recorded && <PendingChips list={pending} onRemove={removeOne} />}

              <Stack spacing={1.5} alignItems="center">
                {DOOR_ENABLED && (
                  <Button component={RouterLink} to={door.to} size="large" sx={DOOR_SX}>
                    {door.label}
                  </Button>
                )}
                {QUIET_LINKS_ENABLED && enAllowed && canAddMore && (
                  <Button variant="text" size="small" onClick={() => setStage("kioku")} sx={QUIET_SX}>
                    {remember}
                  </Button>
                )}
                {QUIET_LINKS_ENABLED && (
                  <Button component={RouterLink} to={quiet.to} variant="text" size="small" sx={QUIET_SX}>
                    {quiet.label}
                  </Button>
                )}
              </Stack>

              {recorded && <Azukari />}
            </>
          )}
        </Box>
      </Box>

      {/* できること（増やしていく）。一つ目: 歴史と人と土地をつなぐ */}
      {!loading && <TochiCategory />}
    </Box>
  );
}
